import { configureStore } from '@reduxjs/toolkit';
import { carePortalMiddleware } from './utils/effectsMiddleware.util';

import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistStore,
} from 'redux-persist';
import { getEnvironment } from 'toolkit/environment/environment/environment.util';

import {
  actingUserReducer,
  alertReducer,
  appNotificationReducer,
  callsReducer,
  changeLogReducer,
  clientsReducer,
  consentsReducer,
  docketReducer,
  feedReducer,
  filesReducer,
  goalsTrackingReducer,
  guideTargetsReducer,
  internalPhoneNumberReducer,
  legacySurveyReducer,
  memberReducer,
  memberStatusChangeRequestReducer,
  memberTransferRequestReducer,
  messageTemplatesReducer,
  messagesReducer,
  metaReducer,
  needReducer,
  needsReducer,
  notesReducer,
  resourceTagsReducer,
  resourcesReducer,
  resourcingReducer,
  scheduledEventsReducer,
  selectedMemberReducer,
  smsMessagesReducer,
  surveyReducer,
  surveyCreatorReducer,
  learnMoreCreatorReducer,
  teamsReducer,
  todoReducer,
  userAdminReducer,
  usersReducer,
  visitsReducer,
  websocketReducer,
  workflowsReducer,
  hospitalizationsReducer,
  bhAppointmentsReducer,
  enrollmentCaseloadReducer,
} from './redux';
import { consentReducer } from './slices/consent.slice';

// createStore is a non-cached version of the getStore function.
// this function is used to get the type of the return value so we
// can use that type (RootStore) to properly type the cachedStore variable
const createStore = () =>
  configureStore({
    reducer: {
      actingUser: actingUserReducer,
      alerts: alertReducer,
      appNotifications: appNotificationReducer,
      changeLogs: changeLogReducer,
      clients: clientsReducer,
      consent: consentReducer,
      consents: consentsReducer, // DEPRECATED
      selectedMember: selectedMemberReducer,
      feed: feedReducer,
      files: filesReducer,
      goalsTracking: goalsTrackingReducer,
      guideTargets: guideTargetsReducer,
      resourceTags: resourceTagsReducer,
      resources: resourcesReducer,
      needs: needsReducer,
      need: needReducer,
      notes: notesReducer,
      visits: visitsReducer,
      calls: callsReducer,
      messages: messagesReducer,
      scheduledEvents: scheduledEventsReducer,
      users: usersReducer,
      legacySurvey: legacySurveyReducer,
      surveys: surveyReducer,
      surveyCreator: surveyCreatorReducer,
      learnMoreCreator: learnMoreCreatorReducer,
      workflows: workflowsReducer,
      todos: todoReducer,
      members: memberReducer,
      memberStatusChangeRequests: memberStatusChangeRequestReducer,
      memberTransferRequests: memberTransferRequestReducer,
      websocket: websocketReducer,
      docket: docketReducer,
      smsMessages: smsMessagesReducer,
      meta: metaReducer,
      resourcing: resourcingReducer,
      userAdmin: userAdminReducer,
      teams: teamsReducer,
      internalPhoneNumbers: internalPhoneNumberReducer,
      hospitalizations: hospitalizationsReducer,
      bhAppointments: bhAppointmentsReducer,
      enrollmentCaseload: enrollmentCaseloadReducer,
      messageTemplates: messageTemplatesReducer,
    },
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat([carePortalMiddleware.middleware]),

    devTools: getEnvironment().production
      ? false
      : {
          maxAge: 5000,
        },
  });

export type RootStore = ReturnType<typeof createStore>;

export const getStore = () => {
  if (!cachedStore) {
    cachedStore = createStore();
    cachedPersistor = persistStore(cachedStore);
  }
  return cachedStore;
};
export const getPersistor = () => {
  if (!cachedPersistor) {
    getStore();
  }
  return cachedPersistor;
};

let cachedStore: RootStore = null;
let cachedPersistor = null;

export type RootState = ReturnType<RootStore['getState']>;
export type AppDispatch = RootStore['dispatch'];
